import React from 'react';
import { convertMarkdownToHTML } from '../utils/markdownUtils';
import { useCollapsibleState } from '../hooks/useCollapsibleState';
import { useSalesforce } from '../hooks/useSalesforce';
import { getFlowSummary, getFlowSummaryWithStructured } from '../utils/flowUtils';

interface WorkspaceAnalysisSectionProps {
  workspace: any;
  flow: any;
  refreshingSummary: boolean;
  onRegenerateSummary: (workspaceId: string) => void;
  index: number;
}

const WorkspaceAnalysisSection: React.FC<WorkspaceAnalysisSectionProps> = ({ 
  workspace, 
  flow, 
  refreshingSummary, 
  onRegenerateSummary,
  index
}) => {
  const { userRole } = useSalesforce({ skipInitialFetch: true });
  const [isExpanded, setIsExpanded] = useCollapsibleState(`workspace-${workspace.id}`, true);
  
  // Get summary and structured data
  const summary = getFlowSummary(flow, workspace.id);
  const { narrative, structured } = getFlowSummaryWithStructured(flow, workspace.id);
  
  const canRefresh = userRole === 'Super-Admin' || userRole === 'Admin' || userRole === 'Editor';
  
  return (
    <div 
      className="bg-white rounded-lg shadow overflow-hidden mb-6"
    >
      <div className="px-4 py-3 sm:px-6 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h3 
              className="text-lg leading-6 font-medium text-gray-900 cursor-pointer flex items-center"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {workspace.name}
            </h3>
          </div>
          
          <div className="flex items-center space-x-2">
            <svg
              className={`w-5 h-5 text-gray-500 transform transition-transform cursor-pointer ${isExpanded ? '' : 'rotate-180'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-4 py-5 sm:p-6 bg-white">
          {refreshingSummary ? (
            <div className="animate-pulse flex space-x-4">
              <div className="flex-1 space-y-4 py-1">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
            </div>
          ) : summary ? (
            <div>
              {/* Show narrative analysis */}
              <div className="prose max-w-none text-left text-gray-900 flow-summary-container">
                <div dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(summary) }} />
              </div>
              
              {/* Show structured data below narrative if available for Performance Analysis */}
              {structured && workspace.name === 'Performance Analysis' && (
                <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Performance Scorecard</h4>
                  
                  {/* Scorecard */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className="bg-white rounded p-3 text-center border">
                      <p className="text-sm text-gray-500">Grade</p>
                      <p className={`text-4xl font-bold ${
                        structured.scorecard?.overallGrade === 'A' ? 'text-green-600' :
                        structured.scorecard?.overallGrade === 'B' ? 'text-green-500' :
                        structured.scorecard?.overallGrade === 'C' ? 'text-yellow-500' :
                        structured.scorecard?.overallGrade === 'D' ? 'text-orange-500' :
                        'text-red-500'
                      }`}>
                        {structured.scorecard?.overallGrade || 'N/A'}
                      </p>
                    </div>
                    
                    <div className="bg-white rounded p-3 text-center border">
                      <p className="text-sm text-gray-500">Query Risk</p>
                      <p className={`text-xl font-semibold ${
                        structured.scorecard?.queryRisk === 'HIGH' ? 'text-red-600' :
                        structured.scorecard?.queryRisk === 'MEDIUM' ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {structured.scorecard?.queryRisk || 'N/A'}
                      </p>
                    </div>
                    
                    <div className="bg-white rounded p-3 text-center border">
                      <p className="text-sm text-gray-500">DML Risk</p>
                      <p className={`text-xl font-semibold ${
                        structured.scorecard?.dmlRisk === 'HIGH' ? 'text-red-600' :
                        structured.scorecard?.dmlRisk === 'MEDIUM' ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {structured.scorecard?.dmlRisk || 'N/A'}
                      </p>
                    </div>
                    
                    <div className="bg-white rounded p-3 text-center border">
                      <p className="text-sm text-gray-500">Bulk Safety</p>
                      <p className={`text-xl font-semibold ${
                        structured.scorecard?.bulkSafety === 'FAIL' ? 'text-red-600' :
                        'text-green-600'
                      }`}>
                        {structured.scorecard?.bulkSafety || 'N/A'}
                      </p>
                    </div>
                    
                    <div className="bg-white rounded p-3 text-center border">
                      <p className="text-sm text-gray-500">CPU Risk</p>
                      <p className={`text-xl font-semibold ${
                        structured.scorecard?.cpuRisk === 'HIGH' ? 'text-red-600' :
                        structured.scorecard?.cpuRisk === 'MEDIUM' ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {structured.scorecard?.cpuRisk || 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Story */}
                  {structured.story && (
                    <div className="mb-4 bg-white rounded p-3 border">
                      <p className="text-sm text-gray-600 font-medium mb-1">Performance Summary</p>
                      <p className="text-sm text-gray-800">{structured.story}</p>
                    </div>
                  )}
                  
                  {/* Analysis Details in two columns */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Key Performance Issue */}
                    {structured.specifics && (
                      <div className="bg-white rounded p-3 border">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Key Performance Issue</p>
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-700"><span className="font-medium text-gray-600">Element:</span> {structured.specifics.element}</p>
                          <p className="text-gray-700"><span className="font-medium text-gray-600">Risk:</span> {structured.specifics.risk}</p>
                          <p className="text-gray-700"><span className="font-medium text-gray-600">Impact:</span> {structured.specifics.impact}</p>
                          <p className="text-gray-700"><span className="font-medium text-gray-600">Location:</span> {structured.specifics.location}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Scorecard Analysis */}
                    {structured.analysis && (
                      <div className="bg-white rounded p-3 border">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Analysis Details</p>
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-700"><span className="font-medium text-gray-600">Query:</span> {structured.analysis.queryDetail}</p>
                          <p className="text-gray-700"><span className="font-medium text-gray-600">DML:</span> {structured.analysis.dmlDetail}</p>
                          <p className="text-gray-700"><span className="font-medium text-gray-600">Bulk:</span> {structured.analysis.bulkDetail}</p>
                          <p className="text-gray-700"><span className="font-medium text-gray-600">CPU:</span> {structured.analysis.cpuDetail}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Analysis Available</h3>
              <p className="mt-1 text-sm text-gray-500">
                This workspace hasn't generated an analysis for this flow yet.
              </p>
              {canRefresh && (
                <div className="mt-4">
                  <button
                    onClick={() => onRegenerateSummary(workspace.id)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Generate Analysis
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkspaceAnalysisSection;